import React, { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { fetchPaymentTransactionsThunk } from "../../redux/slices/paymentSlice";
import jsPDF from "jspdf";
import html2canvas from "html2canvas";
import BackdropSpinner from "../../components/backdropSpinner";
import { LuDownload } from "react-icons/lu";

const PaymentTransactions = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const tableRef = useRef(null);
  const userId = localStorage.getItem("userID");
  const { transactions, transactionsStatus } = useSelector(
    (state) => state.payment
  );
  const [dateRange, setDateRange] = useState("thisMonth");
  const [fromDate, setFromDate] = useState("");
  const [loading, setLoading] = useState(false);
  const [toDate, setToDate] = useState("");
  const [errors, setError] = useState("");

  useEffect(() => {
    if (userId) {
      const payload = { userId, dateRange };
      dispatch(fetchPaymentTransactionsThunk(payload));
    }
  }, [dispatch, userId]);

  const formatDate = (date) => {
    const d = new Date(date);
    const year = d.getFullYear();
    const month = String(d.getMonth() + 1).padStart(2, "0"); // Ensure 2 digits
    const day = String(d.getDate()).padStart(2, "0"); // Ensure 2 digits
    return `${year}-${month}-${day}`;
  };

  const handleFilterPaymemnts = () => {
    const today = new Date();
    const start_date = new Date(fromDate);
    const end_date = new Date(toDate);

    // Validate if dates are in the future
    if (fromDate && start_date > today) {
      setError(t("ticketDetails.errors.fromDateFuture"));
      return;
    }
    if (toDate && end_date > today) {
      setError(t("ticketDetails.errors.toDateFuture"));
      return;
    }

    // Validate if "To" date is before "From" date
    if (fromDate && toDate && start_date > end_date) {
      setError(t("ticketDetails.errors.toDateBeforeFromDate"));
      return;
    }

    // Clear error and proceed
    setError("");
    if (fromDate && toDate) {
      dispatch(
        fetchPaymentTransactionsThunk({
          userId: userId,
          start_date: formatDate(fromDate),
          end_date: formatDate(toDate),
        })
      );
    }
  };

  const handleClearFilters = () => {
    setFromDate("");
    setToDate("");
    setError("");
  };

  const handleDateRangeChange = (range) => {
    setDateRange(range);
    if (range !== "custom") {
      // Automatically dispatch when 'thisMonth' or 'last3Months' is selected
      const payload = { userId, dateRange: range };
      dispatch(fetchPaymentTransactionsThunk(payload));
      handleClearFilters();
    }
  };

  const generatePDF = async () => {
    const input = tableRef.current;
    if (!input) return;
  
    setLoading(true); // Start loading
  
    try {
      // Capture the content of the input element
      const canvas = await html2canvas(input, {
        scale: 3, // High resolution
        useCORS: true, // Handle cross-origin images
        scrollX: -window.scrollX,
        scrollY: -window.scrollY,
      });
  
      const imgData = canvas.toDataURL("image/png");
      const pdf = new jsPDF("p", "mm", "a4");
      const pdfWidth = pdf.internal.pageSize.getWidth();
      const pageHeight = pdf.internal.pageSize.getHeight();
      const imgHeight = (canvas.height * pdfWidth) / canvas.width;
  
      // Check if the content fits within a single page
      if (imgHeight <= pageHeight) {
        pdf.addImage(imgData, "PNG", 0, 0, pdfWidth, imgHeight);
      } else {
        // Content requires multiple pages
        let heightLeft = canvas.height;
        let position = 0;
  
        while (heightLeft > 0) {
          const yOffset = Math.abs(position); // Positive offset for page slicing
          const canvasSlice = document.createElement("canvas");
          canvasSlice.width = canvas.width;
          canvasSlice.height = Math.min(
            (pageHeight * canvas.width) / pdfWidth,
            heightLeft
          );
  
          const context = canvasSlice.getContext("2d");
          context.drawImage(
            canvas,
            0,
            yOffset,
            canvas.width,
            canvasSlice.height,
            0,
            0,
            canvas.width,
            canvasSlice.height
          );
  
          const sliceImgData = canvasSlice.toDataURL("image/png");
          pdf.addImage(
            sliceImgData,
            "PNG",
            0,
            0,
            pdfWidth,
            (canvasSlice.height * pdfWidth) / canvas.width
          );
  
          heightLeft -= canvasSlice.height;
          position -= canvasSlice.height;
  
          if (heightLeft > 0) {
            pdf.addPage();
          }
        }
      }
  
      pdf.save("PaymentTransactions.pdf");
    } catch (error) {
      console.error("Error generating PDF:", error);
    } finally {
      setLoading(false); // Ensure loading state is reset even if an error occurs
    }
  };

  if (transactionsStatus === "loading" || loading) {
    return (
      <div>
        <BackdropSpinner />
      </div>
    );
  }  

  return (
    <>
      <div className="flex flex-col items-center my-5 space-y-3">
        {/* Title */}
        <div className="flex justify-center my-3 flex-row gap-5">
          <p className="text-white text-lg sm:text-xl md:text-2xl font-medium text-center">
            {t("lotteryAccountDetails.headerTitle")}
          </p>
          {/* Download Icon Button */}
          <div className="flex justify-end">
            <button onClick={generatePDF}>
              <LuDownload className="text-[#E2A32C] text-3xl cursor-pointer " />
            </button>
          </div>
        </div>

        {/* Date Range Buttons */}
        <div className="flex flex-wrap justify-center gap-3 w-full px-4">
          {["thisMonth", "last3Months", "custom"].map((range) => (
            <button
              key={range}
              className={`p-2 px-3 sm:px-5 rounded-lg font-semibold text-sm sm:text-base transition-all duration-300 ${
                dateRange === range
                  ? "bg-gradient-to-r from-[#C38B20] to-[#E5AA36] text-black shadow-lg"
                  : "bg-gray-800 text-white hover:bg-gray-700"
              }`}
              onClick={() => handleDateRangeChange(range)}
            >
              {t(`paymentTransactions.${range}`)}
            </button>
          ))}
        </div>
      </div>

      {dateRange === "custom" && (
          <div>
            <div className="flex flex-row justify-center items-center gap-2 lg:gap-4 m-4 lg:mx-20">
              {/* Start Date */}
              <div className="w-full sm:w-1/2 md:w-full lg:w-full">
                <label
                  htmlFor="start"
                  className="block text-xs sm:text-sm font-medium text-[#FFBF4D] mb-1"
                >
                  {t("paymentTransactions.startDate")}
                </label>
                <input
                  type="date"
                  className="w-full lg:w-full md:w-full sm:w-1/2 p-2 rounded border border-[#E2A32C] text-black"
                  placeholder="From"
                  value={fromDate}
                  onChange={(e) => setFromDate(e.target.value)}
                />
              </div>

              {/* End Date */}
              <div className="w-full sm:w-1/2 md:w-full lg:w-full">
                <label
                  htmlFor="end"
                  className="block text-xs sm:text-sm font-medium text-[#FFBF4D] mb-1"
                >
                  {t("paymentTransactions.endDate")}
                </label>
                <input
                  type="date"
                  className="w-full lg:w-full md:w-full sm:w-1/2 p-2 rounded border border-[#E2A32C] text-black"
                  placeholder="To"
                  value={toDate}
                  onChange={(e) => setToDate(e.target.value)}
                />
              </div>
            </div>

            {/* Display validation error */}
            {errors && <div className="text-red-500 mb-4">{errors}</div>}

            {/* Apply Filter Button */}
            <div className="w-full flex justify-center mb-3">
              <button
                onClick={handleFilterPaymemnts}
                className="bg-gradient-to-r from-[#C38B20] to-[#E5AA36] text-black font-semibold p-2 px-5 rounded-lg shadow-lg hover:bg-gradient-to-l transition-all duration-300"
              >
                {t("paymentTransactions.applyFilter")}
              </button>
            </div>
          </div>
        )}

      <div className="px-4 sm:px-10 mb-4">
        <div ref={tableRef}>
          <div className="container mx-auto">
            {!transactions ||
            !transactions.data ||
            transactions.data.length === 0 ? (
              <div className="flex items-center justify-center mt-40 text-center text-lg text-gray-600">
                {t("paymentTransactions.noTransactions")}
              </div>
            ) : (
              <table
                id="transactionsTable"
                className="table-auto w-full max-w-full mx-auto bg-white border border-gray-200 text-[6px] sm:text-[18px] md:text-[12px]"
              >
                <thead>
                  <tr className="bg-gray-200 text-black">
                    <th className="text-left px-1 py-1">
                      {t("paymentTransactions.date")}
                    </th>
                    <th className="text-left px-1 py-1">
                      {t("paymentTransactions.amount")}
                    </th>
                    <th className="text-left px-1 py-1">
                      {t("paymentTransactions.beforeBalance")}
                    </th>
                    <th className="text-left px-1 py-1">
                      {t("paymentTransactions.afterBalance")}
                    </th>
                    <th className="text-left px-1 py-1">
                      {t("paymentTransactions.transactionFor")}
                    </th>
                    <th className="text-left px-1 py-1">
                      {t("paymentTransactions.transactionType")}
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {transactions.data.map((transaction, index) => {
                    const rowClass =
                      transaction.transaction_type === "debit"
                        ? "text-red-500"
                        : transaction.transaction_type === "credit"
                        ? "text-green-500"
                        : "";

                    const formattedAmount =
                      transaction.transaction_type === "debit"
                        ? `- ${transaction.amount}`
                        : transaction.transaction_type === "credit"
                        ? `+ ${transaction.amount}`
                        : transaction.amount;

                    return (
                      <tr
                        key={transaction.id}
                        className={`border-b hover:bg-gray-100 ${
                          index % 2 === 0 ? "bg-gray-50" : "bg-white"
                        }`}
                      >
                        <td className={`px-1 py-1 ${rowClass} text-black`}>
                          {transaction.created_at
                            ? new Date(
                                transaction.created_at
                              ).toLocaleDateString("en-GB", {
                                day: "2-digit",
                                month: "2-digit",
                                year: "numeric",
                              })
                            : "-"}
                        </td>
                        <td className={`px-2 py-1 ${rowClass} text-black`}>
                          {formattedAmount}
                        </td>
                        <td className={`px-2 py-1 ${rowClass} text-black`}>
                          {transaction.before_balance}
                        </td>
                        <td className={`px-2 py-1 ${rowClass} text-black`}>
                          {transaction.after_balance}
                        </td>
                        <td className={`px-2 py-1 ${rowClass} text-black`}>
                          {transaction.transaction_for}
                        </td>
                        <td className={`px-2 py-1 ${rowClass} text-black`}>
                          {transaction.transaction_type}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            )}
          </div>
        </div>
      </div>
    </>
  );
};

export default PaymentTransactions;
